import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import DesignCardPreview from '../components/DesignCardPreview';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Loader2, Trash2, ShoppingCart, Edit, Eye, ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MyDesigns = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewDesign, setPreviewDesign] = useState(null);
  const [previewSide, setPreviewSide] = useState('front');
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (user) loadDesigns();
  }, [user]);

  const loadDesigns = async () => {
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/api/card-designer/my-designs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setDesigns(await res.json());
    } catch (e) { console.error('Failed to load designs', e); }
    finally { setLoading(false); }
  };

  const deleteDesign = async (designId) => {
    setDeleting(designId);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/api/card-designer/my-designs/${designId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setDesigns(prev => prev.filter(d => d.id !== designId));
        toast({ title: "Design deleted" });
      }
    } catch (e) {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    } finally { setDeleting(null); }
  };

  const handleAddToCart = async (design) => {
    try {
      const token = await user.getIdToken();
      await fetch(`${API_URL}/api/card-designer/add-to-cart?design_id=${design.id}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      toast({ title: "Added to cart!" });
      navigate('/cart');
    } catch (e) {
      toast({ title: "Error", description: "Failed to add to cart", variant: "destructive" });
    }
  };

  const handleEditDesign = (design) => {
    const designParam = `?design_id=${design.id}`;
    if (design.category_slug && design.template_slug) {
      navigate(`/shop/${design.category_slug}/${design.template_slug}${designParam}`);
    } else {
      navigate(`/customize/${design.template_id}${designParam}`);
    }
  };

  if (!user) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <p className="text-gray-500">Please sign in to view your designs</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 py-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900" data-testid="my-designs-title">My Designs</h1>
                <p className="text-sm text-gray-500">Your saved card customizations</p>
              </div>
            </div>
            <Button onClick={() => navigate('/shop')} className="bg-amber-500 hover:bg-amber-600 text-sm" data-testid="browse-templates-btn">
              Browse Templates
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-16"><Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-500" /></div>
          ) : designs.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Edit className="w-7 h-7 text-gray-400" />
              </div>
              <h3 className="font-semibold text-gray-700 mb-1">No saved designs yet</h3>
              <p className="text-sm text-gray-500 mb-4">Browse templates and start customizing your NFC business card</p>
              <Button onClick={() => navigate('/shop')} className="bg-amber-500 hover:bg-amber-600">Go to Shop</Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {designs.map(d => (
                <Card key={d.id} className="overflow-hidden group hover:shadow-lg transition-shadow" data-testid={`my-design-${d.id}`}>
                  {/* Card preview */}
                  <div className="relative">
                    <DesignCardPreview
                      design={d}
                      side="front"
                      className="rounded-t-lg"
                      onClick={() => { setPreviewDesign(d); setPreviewSide('front'); }}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <Button size="sm" variant="secondary" className="shadow-lg" onClick={() => { setPreviewDesign(d); setPreviewSide('front'); }}>
                        <Eye className="w-4 h-4 mr-1" />Preview
                      </Button>
                    </div>
                  </div>
                  {/* Info */}
                  <div className="p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-sm text-gray-800 truncate">{d.template_name || 'Custom Design'}</h3>
                        <p className="text-xs text-gray-400">
                          {new Date(d.updated_at || d.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                      {d.template_data?.price && (
                        <span className="text-sm font-bold text-gray-900">&#8377;{d.template_data.price}</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="flex-1 text-xs h-8" onClick={() => handleEditDesign(d)} data-testid={`edit-design-${d.id}`}>
                        <Edit className="w-3 h-3 mr-1" />Edit
                      </Button>
                      <Button size="sm" className="flex-1 text-xs h-8 bg-amber-500 hover:bg-amber-600" onClick={() => handleAddToCart(d)} data-testid={`cart-design-${d.id}`}>
                        <ShoppingCart className="w-3 h-3 mr-1" />Cart
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => deleteDesign(d.id)} disabled={deleting === d.id} data-testid={`delete-design-${d.id}`}>
                        {deleting === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      <Dialog open={!!previewDesign} onOpenChange={(open) => { if (!open) setPreviewDesign(null); }}>
        <DialogContent className="sm:max-w-2xl p-0 overflow-hidden">
          <DialogHeader className="px-5 pt-5 pb-0">
            <DialogTitle className="text-base">{previewDesign?.template_name || 'Design Preview'}</DialogTitle>
          </DialogHeader>
          <div className="p-5 space-y-3">
            {/* Side toggle */}
            <div className="flex justify-center gap-1">
              <button onClick={() => setPreviewSide('front')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${previewSide === 'front' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600'}`}
                data-testid="preview-front-btn">Front</button>
              <button onClick={() => setPreviewSide('back')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${previewSide === 'back' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600'}`}
                data-testid="preview-back-btn">Back</button>
            </div>
            {/* Card render */}
            {previewDesign && (
              <div className="rounded-xl overflow-hidden shadow-xl border max-w-lg mx-auto">
                <DesignCardPreview design={previewDesign} side={previewSide} />
              </div>
            )}
            {/* Actions */}
            <div className="flex gap-2 justify-center pt-2">
              <Button variant="outline" size="sm" onClick={() => { if (previewDesign) handleEditDesign(previewDesign); setPreviewDesign(null); }} data-testid="preview-edit-btn">
                <Edit className="w-4 h-4 mr-1" />Edit Design
              </Button>
              <Button size="sm" className="bg-amber-500 hover:bg-amber-600" onClick={() => { if (previewDesign) handleAddToCart(previewDesign); setPreviewDesign(null); }} data-testid="preview-cart-btn">
                <ShoppingCart className="w-4 h-4 mr-1" />Add to Cart
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default MyDesigns;
